import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sparkles, Eye, EyeOff, MailCheck, RefreshCw, AlertCircle } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { SeoHead } from "@/components/seo/SeoHead";
import { supabase } from "@/integrations/supabase/client";
import {
  recordFailedAttempt,
  resetAttempts,
  useAuthCooldown,
} from "@/lib/auth-attempt-limiter";
import {
  AUTH_PASSWORD_RESET_SENT_DESCRIPTION,
  AUTH_SIGNUP_EMAIL_UNAVAILABLE_DESCRIPTION,
  AUTH_SIGNUP_EMAIL_UNAVAILABLE_TITLE,
  getAuthErrorMessage,
} from "@/lib/auth-messages";
import { TERMS_VERSION } from "@/lib/terms-metadata";
import { recordTermsAcceptance } from "@/lib/record-terms-acceptance";

// Estilo invisível para o honeypot — fora da tela, sem afetar layout.
const honeypotStyle: React.CSSProperties = {
  position: "absolute",
  left: "-9999px",
  width: "1px",
  height: "1px",
  opacity: 0,
  pointerEvents: "none",
};

const Auth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [signupName, setSignupName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupEmailConfirm, setSignupEmailConfirm] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupPasswordConfirm, setSignupPasswordConfirm] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupPasswordConfirm, setShowSignupPasswordConfirm] = useState(false);
  const [signupAcceptedTerms, setSignupAcceptedTerms] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [showSignupUnavailable, setShowSignupUnavailable] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [searchParams] = useSearchParams();
  const authTabFromUrl = searchParams.get("tab") === "signup" ? "signup" : "login";
  const [authTab, setAuthTab] = useState<"login" | "signup">(authTabFromUrl);
  
  // Honeypot fields (devem ficar vazios — humanos não veem)
  const [loginHoneypot, setLoginHoneypot] = useState("");
  const [signupHoneypot, setSignupHoneypot] = useState("");
  const [resetHoneypot, setResetHoneypot] = useState("");

  // Cooldown anti-brute-force (5 falhas → 60s) por email + ação
  const loginCooldown = useAuthCooldown("login", loginEmail);
  const signupCooldown = useAuthCooldown("signup", signupEmail);
  const resetCooldown = useAuthCooldown("reset", resetEmail);

  const { user, signIn, signUp, signInWithGoogle, resetPassword, resendConfirmationEmail } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Handle resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Handle successful checkout return
  useEffect(() => {
    const handleCheckoutReturn = async () => {
      const sessionId = searchParams.get("session_id");
      const pendingCheckout = localStorage.getItem("pendingCheckout");
      const selectedPlan = localStorage.getItem("selectedPlan");

      if (sessionId && pendingCheckout && selectedPlan && !user) {
        localStorage.removeItem("pendingCheckout");
        
        toast({
          title: "Pagamento processado!",
          description: "Complete o cadastro para acessar sua conta.",
        });
      }
    };

    handleCheckoutReturn();
  }, [searchParams, user, toast]);

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "signup" || tab === "login") {
      setAuthTab(tab);
    }
  }, [searchParams]);

  // Check if there's a pending plan selection
  useEffect(() => {
    const pendingPlan = searchParams.get("plan");
    if (user && pendingPlan) {
      // User just signed up and has a pending plan, redirect to pricing
      navigate(`/#pricing`);
      return;
    }

    if (user) {
      const next = searchParams.get("next");
      if (next && next.startsWith("/") && !next.startsWith("//")) {
        navigate(next, { replace: true });
        return;
      }
      navigate("/tasks");
    }
  }, [user, searchParams, navigate]);

  // Handle OAuth (Google) error messages returned via URL hash
  useEffect(() => {
    if (typeof window === "undefined") return;

    const hash = window.location.hash;
    if (hash && hash.includes("error=")) {
      const params = new URLSearchParams(hash.slice(1));
      const error = params.get("error");
      const description = params.get("error_description");

      toast({
        title: "Erro ao entrar com Google",
        description: description || error || "Ocorreu um erro ao conectar com o Google.",
        variant: "destructive",
      });

      // Clear hash to avoid repeated toasts on reload
      window.location.hash = "";
    }
  }, [toast]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot: bot preencheu o campo invisível — finge sucesso e não chama backend
    if (loginHoneypot) {
      toast({ title: "Entrando..." });
      return;
    }

    if (loginCooldown > 0) {
      toast({
        title: "Muitas tentativas",
        description: `Aguarde ${loginCooldown}s antes de tentar novamente.`,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    const { error } = await signIn(loginEmail, loginPassword);

    if (error) {
      const next = recordFailedAttempt("login", loginEmail);
      const blockedFor = next.blockedUntil ? Math.ceil((next.blockedUntil - Date.now()) / 1000) : 0;
      toast({
        title: "Erro ao entrar",
        description: blockedFor > 0
          ? `Muitas tentativas. Aguarde ${blockedFor}s.`
          : getAuthErrorMessage(error),
        variant: "destructive",
      });
    } else {
      resetAttempts("login", loginEmail);
    }

    setIsLoading(false);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot
    if (signupHoneypot) {
      toast({ title: "Conta criada!", description: "Verifique seu email." });
      return;
    }

    if (signupCooldown > 0) {
      toast({
        title: "Muitas tentativas",
        description: `Aguarde ${signupCooldown}s antes de tentar novamente.`,
        variant: "destructive",
      });
      return;
    }


    const normalizedEmail = signupEmail.trim().toLowerCase();
    const normalizedEmailConfirm = signupEmailConfirm.trim().toLowerCase();

    if (!normalizedEmail || normalizedEmail !== normalizedEmailConfirm) {
      toast({
        title: "Emails não coincidem",
        description: "Por favor, confirme o email corretamente para continuar.",
        variant: "destructive",
      });
      return;
    }

    // Validate password confirmation
    if (signupPassword !== signupPasswordConfirm) {
      toast({
        title: "Senhas não coincidem",
        description: "Por favor, verifique se as senhas digitadas são iguais.",
        variant: "destructive",
      });
      return;
    }

    if (signupPassword.length < 6) {
      toast({
        title: "Senha muito curta",
        description: "A senha deve ter no mínimo 6 caracteres.",
        variant: "destructive",
      });
      return;
    }

    if (!signupAcceptedTerms) {
      toast({
        title: "Aceite os termos para continuar",
        description: "Para criar sua conta, você precisa aceitar os Termos de Uso e a Política de Privacidade.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error, data, signupEmailUnavailable } = await signUp(signupEmail, signupPassword, signupName);

      if (error) {
        const next = recordFailedAttempt("signup", signupEmail);
        const blockedFor = next.blockedUntil ? Math.ceil((next.blockedUntil - Date.now()) / 1000) : 0;
        toast({
          title: "Erro ao criar conta",
          description: blockedFor > 0
            ? `Muitas tentativas. Aguarde ${blockedFor}s.`
            : getAuthErrorMessage(error),
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      if (signupEmailUnavailable) {
        resetAttempts("signup", signupEmail);
        setLoginEmail(signupEmail);
        setShowSignupUnavailable(true);
        setIsLoading(false);
        return;
      }

      resetAttempts("signup", signupEmail);

      // Save terms acceptance for manual signup (somente quando há usuário novo)
      if (data?.user?.id && signupAcceptedTerms) {
        await recordTermsAcceptance(data.user.id, "signup");
      }

      setConfirmationEmail(signupEmail);
      setShowEmailConfirmation(true);

    } catch (err) {
      console.error("Signup error:", err);
      toast({
        title: "Erro ao criar conta",
        description: "Ocorreu um erro inesperado. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    setResendCooldown(60); // 60 seconds cooldown
    
    const { error } = await resendConfirmationEmail(confirmationEmail);
    
    if (error) {
      toast({
        title: "Erro ao reenviar email",
        description: getAuthErrorMessage(error),
        variant: "destructive",
      });
    } else {
      toast({
        title: "Email reenviado!",
        description: "Verifique sua caixa de entrada.",
      });
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    const { error } = await signInWithGoogle();
    
    if (error) {
      const friendlyMessage = getAuthErrorMessage(error);
      toast({
        title: "Erro ao entrar com Google",
        description: friendlyMessage,
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot
    if (resetHoneypot) {
      toast({ title: "Email enviado!", description: "Verifique sua caixa de entrada." });
      setResetDialogOpen(false);
      setResetEmail("");
      return;
    }

    if (resetCooldown > 0) {
      toast({
        title: "Muitas tentativas",
        description: `Aguarde ${resetCooldown}s antes de tentar novamente.`,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    const { error } = await resetPassword(resetEmail);

    if (error) {
      const next = recordFailedAttempt("reset", resetEmail);
      const blockedFor = next.blockedUntil ? Math.ceil((next.blockedUntil - Date.now()) / 1000) : 0;
      toast({
        title: "Não foi possível enviar",
        description: blockedFor > 0
          ? `Muitas tentativas. Aguarde ${blockedFor}s.`
          : getAuthErrorMessage(error),
        variant: "destructive",
      });
    } else {
      resetAttempts("reset", resetEmail);
      toast({
        title: "Verifique seu email",
        description: AUTH_PASSWORD_RESET_SENT_DESCRIPTION,
      });
      setResetDialogOpen(false);
      setResetEmail("");
    }

    setIsLoading(false);
  };

  // Email indisponível ou já em uso (anti-enumeração — mesma mensagem para ambos)
  if (showSignupUnavailable) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/20 p-4">
        <SeoHead title="Login e Cadastro | FabChat" description="Acesse sua conta FabChat ou crie uma nova. Gerencie agentes de IA para WhatsApp e Instagram com CRM integrado." canonicalPath="/auth" noindex />
        <Link to="/" className="flex items-center justify-center font-bold text-2xl mb-8">
          <img src="/logo-light.png?v=6" alt="FabChat Logo" className="h-10 sm:h-12 w-auto object-contain block dark:hidden" />
          <img src="/logo.png?v=6" alt="FabChat Logo" className="h-10 sm:h-12 w-auto object-contain hidden dark:block" />
        </Link>

        <Card className="w-full max-w-md shadow-lg relative z-50">
          <CardContent className="pt-6 pb-6 flex flex-col items-center text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold">{AUTH_SIGNUP_EMAIL_UNAVAILABLE_TITLE}</h2>
              <p className="text-muted-foreground">
                {AUTH_SIGNUP_EMAIL_UNAVAILABLE_DESCRIPTION}
              </p>
            </div>

            <div className="w-full space-y-3">
              <Button
                className="w-full"
                onClick={() => {
                  setShowSignupUnavailable(false);
                  setAuthTab("login");
                }}
              >
                Ir para login
              </Button>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setShowSignupUnavailable(false);
                  setAuthTab("signup");
                  setSignupEmail("");
                  setSignupEmailConfirm("");
                  setSignupPassword("");
                  setSignupPasswordConfirm("");
                }}
              >
                Tentar com outro email
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Email confirmation screen (somente quando o cadastro foi aceito e o email foi enviado)
  if (showEmailConfirmation) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/20 p-4">
        <SeoHead title="Login e Cadastro | FabChat" description="Acesse sua conta FabChat ou crie uma nova. Gerencie agentes de IA para WhatsApp e Instagram com CRM integrado." canonicalPath="/auth" noindex />
      <Link to="/" className="flex items-center justify-center font-bold text-2xl mb-8">
        <img src="/logo-light.png?v=6" alt="FabChat Logo" className="h-10 sm:h-12 w-auto object-contain block dark:hidden" />
        <img src="/logo.png?v=6" alt="FabChat Logo" className="h-10 sm:h-12 w-auto object-contain hidden dark:block" />
      </Link>

        <Card className="w-full max-w-md shadow-lg relative z-50">
          <CardContent className="pt-6 pb-6 flex flex-col items-center text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <MailCheck className="w-8 h-8 text-primary" />
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-bold">Verifique seu email</h2>
              <p className="text-muted-foreground">
                Enviamos um link de verificação para{" "}
                <span className="font-semibold text-foreground">{confirmationEmail}</span>
              </p>
              <p className="text-sm text-muted-foreground mt-4">
                Por favor, acesse sua caixa de entrada e clique no link para ativar sua conta.
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                💡 Não encontrou o email? Verifique sua pasta de spam.
              </p>
            </div>

            <div className="w-full space-y-3">
              <Button
                variant="outline"
                className="w-full"
                onClick={handleResendConfirmation}
                disabled={resendCooldown > 0}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {resendCooldown > 0 
                  ? `Reenviar em ${resendCooldown}s`
                  : "Reenviar email"
                }
              </Button>

              <Button
                variant="ghost"
                className="w-full"
                onClick={() => setShowEmailConfirmation(false)}
              >
                Voltar para login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/20 p-4">
      <SeoHead title="Login e Cadastro | FabChat" description="Acesse sua conta FabChat ou crie uma nova. Gerencie agentes de IA para WhatsApp e Instagram com CRM integrado." canonicalPath="/auth" noindex />
      <Link to="/" className="flex items-center justify-center font-bold text-2xl mb-8">
        <img src="/logo-light.png?v=6" alt="FabChat Logo" className="h-10 sm:h-12 w-auto object-contain block dark:hidden" />
        <img src="/logo.png?v=6" alt="FabChat Logo" className="h-10 sm:h-12 w-auto object-contain hidden dark:block" />
      </Link>

      <Card className="w-full max-w-md shadow-lg relative z-50">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Bem-vindo</CardTitle>
          <CardDescription>
            Entre ou crie sua conta para começar
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <Tabs
            value={authTab}
            onValueChange={(value) => setAuthTab(value as "login" | "signup")}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="login">Entrar</TabsTrigger>
              <TabsTrigger value="signup">Criar conta</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                {/* Honeypot anti-bot — invisível para humanos */}
                <div style={honeypotStyle} aria-hidden="true">
                  <Label htmlFor="login-website">Website</Label>
                  <Input
                    id="login-website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={loginHoneypot}
                    onChange={(e) => setLoginHoneypot(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    name="email"
                    type="email"
                    autoComplete="username"
                    placeholder="seu@email.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Senha</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      name="password"
                      type={showLoginPassword ? "text" : "password"}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                    >
                      {showLoginPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                  <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
                    <DialogTrigger asChild>
                      <Button type="button" variant="link" className="px-0 text-xs text-primary">
                        Esqueceu sua senha?
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Recuperar Senha</DialogTitle>
                        <DialogDescription>
                          Informe seu email. Se existir uma conta associada, você receberá um link de recuperação.
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handlePasswordReset} className="space-y-4">
                        {/* Honeypot anti-bot */}
                        <div style={honeypotStyle} aria-hidden="true">
                          <Label htmlFor="reset-website">Website</Label>
                          <Input
                            id="reset-website"
                            name="website"
                            type="text"
                            tabIndex={-1}
                            autoComplete="off"
                            value={resetHoneypot}
                            onChange={(e) => setResetHoneypot(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="reset-email">Email</Label>
                          <Input
                            id="reset-email"
                            name="email"
                            type="email"
                            autoComplete="email"
                            placeholder="seu@email.com"
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                            required
                          />
                        </div>
                        <Button type="submit" className="w-full" disabled={isLoading || resetCooldown > 0}>
                          {resetCooldown > 0
                            ? `Aguarde ${resetCooldown}s`
                            : isLoading ? "Enviando..." : "Enviar link de recuperação"}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
                <Button type="submit" className="w-full" disabled={isLoading || loginCooldown > 0}>
                  {loginCooldown > 0
                    ? `Aguarde ${loginCooldown}s`
                    : isLoading ? "Entrando..." : "Entrar"}
                </Button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Ou continue com
                  </span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={handleGoogleLogin}
                disabled={isLoading}
                className="w-full"
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Entrar com Google
              </Button>
            </TabsContent>
            
            <TabsContent value="signup">
              <form onSubmit={handleSignup} className="space-y-4">
                {/* Honeypot anti-bot */}
                <div style={honeypotStyle} aria-hidden="true">
                  <Label htmlFor="signup-website">Website</Label>
                  <Input
                    id="signup-website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={signupHoneypot}
                    onChange={(e) => setSignupHoneypot(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-name">Nome completo</Label>
                  <Input
                    id="signup-name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    placeholder="Seu nome"
                    value={signupName}
                    onChange={(e) => setSignupName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="seu@email.com"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email-confirm">Confirmar Email</Label>
                  <Input
                    id="signup-email-confirm"
                    name="email-confirm"
                    type="email"
                    autoComplete="email"
                    placeholder="seu@email.com"
                    value={signupEmailConfirm}
                    onChange={(e) => setSignupEmailConfirm(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Senha</Label>
                  <div className="relative">
                    <Input
                      id="signup-password"
                      name="new-password"
                      type={showSignupPassword ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="••••••••"
                      value={signupPassword}
                      onChange={(e) => setSignupPassword(e.target.value)}
                      required
                      minLength={6}
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowSignupPassword(!showSignupPassword)}
                    >
                      {showSignupPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Mínimo 6 caracteres</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password-confirm">Confirmar Senha</Label>
                  <div className="relative">
                    <Input
                      id="signup-password-confirm"
                      name="new-password-confirm"
                      type={showSignupPasswordConfirm ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="••••••••"
                      value={signupPasswordConfirm}
                      onChange={(e) => setSignupPasswordConfirm(e.target.value)}
                      required
                      minLength={6}
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowSignupPasswordConfirm(!showSignupPasswordConfirm)}
                    >
                      {showSignupPasswordConfirm ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="rounded-md border border-border bg-muted/30 p-3">
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="signup-accept-terms"
                      checked={signupAcceptedTerms}
                      onCheckedChange={(checked) => setSignupAcceptedTerms(checked === true)}
                      className="mt-0.5"
                    />
                    <Label
                      htmlFor="signup-accept-terms"
                      className="text-xs leading-relaxed text-muted-foreground font-normal"
                    >
                      Eu li e <strong>aceito eletronicamente</strong> os{" "}
                      <Link to="/termos" className="text-primary hover:underline font-medium">
                        Termos de Serviço
                      </Link>{" "}
                      (versão {TERMS_VERSION}), incluindo regras de assinatura e cobrança, e a{" "}
                      <Link to="/privacidade" className="text-primary hover:underline font-medium">
                        Política de Privacidade
                      </Link>
                      .
                    </Label>
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={isLoading || signupCooldown > 0}>
                  {signupCooldown > 0
                    ? `Aguarde ${signupCooldown}s`
                    : isLoading ? "Criando conta..." : "Criar conta"}
                </Button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Ou continue com
                  </span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (!signupAcceptedTerms) {
                    toast({
                      title: "Aceite os termos para continuar",
                      description: "Para criar sua conta, você precisa aceitar os Termos de Uso e a Política de Privacidade.",
                      variant: "destructive",
                    });
                    return;
                  }
                  handleGoogleLogin();
                }}
                disabled={isLoading}
                className="w-full"
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Cadastrar com Google
              </Button>
            </TabsContent>
          </Tabs>
        </CardContent>
        
        <CardFooter className="flex flex-col gap-4">
          <div className="text-sm text-muted-foreground text-center">
            Ao continuar, você concorda com nossos{" "}
            <Link to="/termos" className="text-primary hover:underline">
              Termos de Uso
            </Link>{" "}
            e{" "}
            <Link to="/privacidade" className="text-primary hover:underline">
              Política de Privacidade
            </Link>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default Auth;
